/**
 * 主题切换 Store
 *
 * 支持产品侧 2 套主题：
 *   - claude（默认）：Anthropic 暖白 + 赤陶橙 + 墨黑（claude.ai 同款素色）
 *   - dark：暗色（黑底浅米）
 *   - wangshu：董事长演示工作台主题（沙金深色）
 *
 * 切换通过 document.documentElement.dataset.theme 实现，
 * claude-tokens.css 内通过 [data-theme="X"] 选择器作用域覆盖 token。
 * Arco 组件语义色同步依赖 document.body 的 arco-theme（由 syncArcoBodyTheme 维护）。
 *
 * 持久化：localStorage('theme')
 */
import { defineStore } from 'pinia'
import { ref } from 'vue'

export type ThemeMode = 'claude' | 'dark' | 'debug' | 'wangshu' | 'wangshu-light'

const THEME_KEY = 'theme'
const LAST_CLAUDE_THEME_KEY = 'theme:lastClaude'
const LAST_WANGSHU_THEME_KEY = 'theme:lastWangshu'
const DEFAULT_THEME: ThemeMode = 'claude'

/**
 * 判断字符串是否为受支持的主题模式。
 */
function isThemeMode(value: string | null | undefined): value is ThemeMode {
  return (
    value === 'claude' ||
    value === 'dark' ||
    value === 'debug' ||
    value === 'wangshu' ||
    value === 'wangshu-light'
  )
}

function isWangshuTheme(theme: ThemeMode): theme is 'wangshu' | 'wangshu-light' {
  return theme === 'wangshu' || theme === 'wangshu-light'
}

/**
 * 将 Arco Design 全局语义色与当前 product 主题对齐。
 *
 * 望舒深色（`wangshu`）仅通过 `data-theme` 换 token，若不同步 `body[arco-theme]`，
 * Arco 组件仍按浅色变量渲染（如公告表单 `AnnouncementFormCard` 发白）。
 */
function syncArcoBodyTheme(theme: ThemeMode): void {
  if (typeof document === 'undefined') {
    return
  }
  const arcoDark = theme === 'dark' || theme === 'wangshu'
  if (arcoDark) {
    document.body.setAttribute('arco-theme', 'dark')
  } else {
    document.body.removeAttribute('arco-theme')
  }
}

function readStoredTheme(key: string, fallback: ThemeMode): ThemeMode {
  try {
    const saved = localStorage.getItem(key)
    return isThemeMode(saved) ? saved : fallback
  } catch {
    return fallback
  }
}

export const useThemeStore = defineStore('theme', () => {
  const current = ref<ThemeMode>(DEFAULT_THEME)

  function applyTheme(theme: ThemeMode): void {
    document.documentElement.dataset.theme = theme
    syncArcoBodyTheme(theme)
    current.value = theme
    try {
      localStorage.setItem(THEME_KEY, theme)
      if (isWangshuTheme(theme)) {
        localStorage.setItem(LAST_WANGSHU_THEME_KEY, theme)
      } else if (theme === 'claude' || theme === 'dark') {
        localStorage.setItem(LAST_CLAUDE_THEME_KEY, theme)
      }
    } catch {
      // 隐私模式可能 setItem 失败，忽略
    }
  }

  function initTheme(): void {
    let theme: ThemeMode = DEFAULT_THEME
    try {
      const saved = localStorage.getItem(THEME_KEY)
      if (saved !== null && isThemeMode(saved)) {
        theme = saved
      }
    } catch {
      // 默认 claude
    }
    applyTheme(theme)
  }

  function toggleTheme(): void {
    // MainLayout 双主题 toggle：claude（亮 = 素色）↔ dark（暗）。
    // 与 WsTopbar 的 wangshu/wangshu-light toggle 行为对称，
    // 让 /agents/workspace 与 /workbench 拥有等价的"亮 ↔ 暗"切换体验。
    // debug 保留为程序化设值（setTheme(...) 显式指定），不进入 toggle 循环。
    const next: ThemeMode = current.value === 'dark' ? 'claude' : 'dark'
    applyTheme(next)
  }

  function setTheme(theme: ThemeMode): void {
    applyTheme(theme)
  }

  function ensureWangshuTheme(): void {
    if (isWangshuTheme(current.value)) {
      return
    }
    const previousWangshuTheme = readStoredTheme(LAST_WANGSHU_THEME_KEY, 'wangshu')
    applyTheme(isWangshuTheme(previousWangshuTheme) ? previousWangshuTheme : 'wangshu')
  }

  function ensureClaudeTheme(): void {
    if (current.value === 'claude' || current.value === 'dark') {
      return
    }
    const previousClaudeTheme = readStoredTheme(LAST_CLAUDE_THEME_KEY, 'claude')
    applyTheme(previousClaudeTheme === 'dark' ? 'dark' : 'claude')
  }

  /**
   * 从 document.documentElement.dataset.theme 同步当前主题状态。
   *
   * 用于处理外部代码直接修改 data-theme 的情况，避免页面实际主题
   * 和顶部主题切换按钮文案不一致。
   */
  function syncFromDocument(): void {
    const domTheme = document.documentElement.dataset.theme
    if (!isThemeMode(domTheme) || domTheme === current.value) {
      return
    }
    current.value = domTheme
    syncArcoBodyTheme(domTheme)
    try {
      localStorage.setItem(THEME_KEY, domTheme)
    } catch {
      // 隐私模式可能 setItem 失败，忽略
    }
  }

  return {
    current,
    initTheme,
    toggleTheme,
    setTheme,
    ensureWangshuTheme,
    ensureClaudeTheme,
    syncFromDocument
  }
})
